import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { CELLS } from "@/constants/testIds";
import { toast, confirmToast } from "@/lib/notify";
import { Plus, Search, User, TrendingUp } from "lucide-react";
import CellsStats from "./cells/CellsStats";
import CellForm from "./cells/CellForm";
import CellRecord from "./cells/CellRecord";
import CellGrowthChart from "@/components/CellGrowthChart";

export default function Cells() {
  const now = new Date();
  const [cells, setCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState(null); // null | "new" | "edit"
  const [showGrowth, setShowGrowth] = useState(false);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [annualOffering, setAnnualOffering] = useState(0);
  const [monthOffering, setMonthOffering] = useState(0);

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    const arr = [];
    for (let i = y + 1; i >= 2022; i--) arr.push(i);
    return arr;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cells");
      setCells(data || []);
    } catch {
      toast.error("Error cargando células");
    } finally {
      setLoading(false);
    }
  };

  const loadOfferings = async () => {
    try {
      const [annualRes, monthRes] = await Promise.all([
        api.get("/cells/captures/summary", { params: { year } }),
        api.get("/cells/captures/summary", { params: { year, month } }),
      ]);
      setAnnualOffering(annualRes.data?.totals?.offering || 0);
      setMonthOffering(monthRes.data?.totals?.offering || 0);
    } catch {
      setAnnualOffering(0);
      setMonthOffering(0);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadOfferings(); }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = cells.find((c) => c.id === selectedId) || null;

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cells;
    return cells.filter(
      (c) =>
        (c.leader_name || "").toLowerCase().includes(q) ||
        (c.name || "").toLowerCase().includes(q) ||
        (c.subleader || "").toLowerCase().includes(q),
    );
  }, [cells, query]);

  const totalMembers = cells.reduce((a, c) => a + (c.member_count || 0), 0);

  const openCell = (id) => {
    setSelectedId(id);
    setMode(null);
  };

  const saveCell = async (payload) => {
    if (mode === "edit" && selected) {
      await api.put(`/cells/${selected.id}`, payload);
      toast.success("Célula modificada");
    } else {
      const { data } = await api.post("/cells", payload);
      toast.success("Célula guardada");
      setSelectedId(data.id);
    }
    setMode(null);
    await load();
  };

  const deleteCell = async () => {
    if (!selected) return;
    const ok = await confirmToast(
      `¿Eliminar la célula "${selected.name}"?`,
      {
        description: "Se eliminarán también sus capturas y se desasignarán sus miembros.",
        confirmLabel: "Eliminar",
      },
    );
    if (!ok) return;
    try {
      await api.delete(`/cells/${selected.id}`);
      toast.success("Célula eliminada");
      setSelectedId(null);
      setMode(null);
      await Promise.all([load(), loadOfferings()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div data-testid={CELLS.page}>
      <PageHeader
        title="Células"
        subtitle="Grupos pequeños · Líderes · Capturas de asistencia"
      />
      <div className="px-6 py-6 max-w-5xl">
        <CellsStats
          totalCells={cells.length}
          activeCells={cells.filter((c) => c.active).length}
          totalMembers={totalMembers}
          annualOffering={annualOffering}
          monthOffering={monthOffering}
          year={year}
          setYear={setYear}
          month={month}
          setMonth={setMonth}
          yearOptions={yearOptions}
        />

        {/* Search + new */}
        <div className="border border-[#d9cfb8] bg-white p-4 mb-6 max-w-2xl">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b7e5e]" />
              <input
                data-testid={CELLS.searchInput}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nombre del líder o de la célula…"
                className="w-full border border-[#d9cfb8] bg-[#fbf7ee] pl-9 pr-3 py-2 text-[13px] rounded focus:border-[#c8a04a] focus:bg-white focus:outline-none"
              />
            </div>
            <button
              data-testid={CELLS.newCellButton}
              onClick={() => { setMode("new"); setSelectedId(null); }}
              className="inline-flex items-center gap-2 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] px-4 py-2 text-[11px] uppercase tracking-widest"
            >
              <Plus size={13} /> Nueva célula
            </button>
          </div>

          {loading ? (
            <div className="mt-3 text-[12px] text-[#8b7e5e]">Cargando…</div>
          ) : results.length === 0 ? (
            <div className="mt-3 text-[12px] text-[#8b7e5e]">
              {cells.length === 0
                ? 'No hay células. Agrega la primera con "Nueva célula".'
                : "Sin coincidencias para la búsqueda."}
            </div>
          ) : (
            <div className="mt-3 divide-y divide-[#e8e1cd] border border-[#e8e1cd] max-h-64 overflow-y-auto">
              {results.map((c) => (
                <button
                  key={c.id}
                  data-testid={CELLS.searchResult(c.id)}
                  onClick={() => openCell(c.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left text-[13px] hover:bg-[#fbf7ee] ${
                    selectedId === c.id ? "bg-[#fdf6e3]" : ""
                  }`}
                >
                  <User size={14} className="shrink-0 text-[#7a5b0f]" />
                  <span className="font-medium text-[#1c1a14]">{c.leader_name || "Sin líder"}</span>
                  <span className="text-[#8b7e5e]">· {c.name}</span>
                  {c.county && <span className="text-[11px] text-[#8b7e5e]">({c.county})</span>}
                  {!c.active && (
                    <span className="ml-auto text-[9px] uppercase tracking-widest text-[#8b7e5e] border border-[#d9cfb8] rounded px-1.5 py-0.5">
                      Inactiva
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {mode === "new" && (
          <CellForm onSave={saveCell} onCancel={() => setMode(null)} />
        )}
        {selected && mode === "edit" && (
          <CellForm cell={selected} onSave={saveCell} onCancel={() => setMode(null)} />
        )}
        {selected && mode === null && (
          <CellRecord
            cell={selected}
            onEdit={() => setMode("edit")}
            onDelete={deleteCell}
            onClose={() => setSelectedId(null)}
            onDataChanged={loadOfferings}
          />
        )}

        {/* Growth chart: only in the main menu (no record open), behind a toggle */}
        {!selected && mode === null && (
          <div className="max-w-2xl">
            <button
              data-testid={CELLS.growthToggle}
              onClick={() => setShowGrowth((s) => !s)}
              className="inline-flex items-center gap-2 border border-[#1c2a3a] px-4 py-2 text-[11px] uppercase tracking-widest text-[#1c2a3a] hover:bg-[#1c2a3a] hover:text-[#e7c879] transition mb-4"
            >
              <TrendingUp size={13} />
              {showGrowth ? "Ocultar crecimiento por condado" : "Ver crecimiento por condado"}
            </button>
            {showGrowth && <CellGrowthChart />}
          </div>
        )}
      </div>
    </div>
  );
}
